/**
 * Everything mechanical: the wheels, the arrival, and the things that move when you move a lever.
 *
 * ```
 *   white ─► lowpass(f ← surface) ─► [AM by a bump LFO at f ← ground speed] ─► roll gain ─┐
 *                                                                                         │
 *   flap servo:  saw + noise ─► lowpass ─► gate(while travelling) ────────────────────────┼─► out
 *   gear servo:  saw + noise ─► lowpass ─► gate(while travelling) ────────────────────────┤
 *                                                                                         │
 *   touchdown:   baked thump ×f(sink) + baked chirp ×f(ground speed)  [one-shots] ────────┘
 * ```
 *
 * ## The rumble is where you find out you are moving
 *
 * Two things drive it and both are load-bearing. Speed sets the level and the *bump rate*: a
 * concrete runway is poured in slabs and the tyres cross an expansion joint every few metres, so
 * the modulation frequency has to scale with ground speed or a landing roll sounds like it is
 * decelerating in level but not in rhythm — which is deeply strange and very hard to place.
 *
 * Weight on wheels sets the rest, and that is the good cue: as the wing takes the load through
 * the rotation the rumble fades out *before* the wheels leave the ground, which is exactly what
 * happens in the aeroplane, and it means you can hear the moment you have flown it off.
 *
 * ## The arrival
 *
 * Two separate one-shots, because they are two separate physical events measuring two different
 * things. The thump is the gear absorbing vertical energy and scales with sink rate. The chirp is
 * stationary rubber being spun up and scales with ground speed. A greased-on wheel landing at
 * 60 kt is a loud chirp and almost no thump; a firm arrival at 45 kt is the reverse. Getting this
 * split right is most of what makes a landing audible as *good* or *bad*.
 *
 * ## Servos
 *
 * A small electric motor under load: a sawtooth in the low hundreds of hertz, plus filtered noise
 * for the gearbox, gated on the surface actually being in transit. The pitch drifts up slightly
 * while running, which is what a motor pulling against increasing air load does. A clunk plays
 * when travel stops, so a flap selection has a beginning and an end rather than just fading out.
 *
 * `updateFrame(alpha)`: {@link GroundVoice.updateFrame}, once per rendered frame. The continuous
 * layers allocate nothing; the one-shots construct nodes by necessity — see `parts.ts`.
 */

import { clamp, clamp01 } from '../../core/math.js';
import type { Rng } from '../../core/rng.js';
import type { AudioBank } from '../bank.js';
import { rollGain, surfaceVoice, touchdownChirpGain, touchdownGain } from '../model.js';
import { setTarget } from '../param.js';
import type { AircraftSoundConfig } from '../soundSet.js';
import type { AircraftAudioState } from '../state.js';
import { OneShotPool, disconnectAll, gainNode, highpass, loopingSource, lowpass } from './parts.js';

/** Metres between bumps on a hard runway — a concrete slab is about this long. */
const BUMP_SPACING_M = 4.6;

export class GroundVoice {
  private readonly context: AudioContext;
  private readonly config: AircraftSoundConfig;
  private readonly bank: AudioBank;

  private readonly rollSource: AudioBufferSourceNode;
  private readonly rollLow: BiquadFilterNode;
  private readonly rollAm: GainNode;
  private readonly bumpOscillator: OscillatorNode;
  private readonly bumpDepth: GainNode;
  private readonly rollLevel: GainNode;

  private readonly flapServo: ServoVoice;
  private readonly gearServo: ServoVoice;

  private readonly oneShots: OneShotPool;
  private readonly output: GainNode;

  /** Edge detectors. Sound is made by *changes*, and these are what notice them. */
  private wasOnGround = true;
  private flapWasMoving = false;
  private gearWasMoving = false;

  constructor(
    context: AudioContext,
    bank: AudioBank,
    destination: AudioNode,
    config: AircraftSoundConfig,
    rng: Rng,
  ) {
    this.context = context;
    this.config = config;
    this.bank = bank;

    this.output = gainNode(context, 1);
    this.output.connect(destination);
    this.oneShots = new OneShotPool(context, this.output, rng, 12);

    // --- rolling rumble --------------------------------------------------------------------
    this.rollLevel = gainNode(context, 0);
    this.rollLevel.connect(this.output);
    this.rollAm = gainNode(context, 1);
    this.rollAm.connect(this.rollLevel);
    this.rollLow = lowpass(context, 800, 1.1);
    this.rollLow.connect(this.rollAm);
    this.rollSource = loopingSource(context, bank.white, bank.whiteLoopEndS, 0.83, 3.4);
    this.rollSource.connect(this.rollLow);

    this.bumpOscillator = context.createOscillator();
    this.bumpOscillator.type = 'sine';
    this.bumpOscillator.frequency.value = 2;
    this.bumpDepth = gainNode(context, 0);
    this.bumpOscillator.connect(this.bumpDepth);
    this.bumpDepth.connect(this.rollAm.gain);
    this.bumpOscillator.start(0);

    // --- servos ------------------------------------------------------------------------------
    this.flapServo = new ServoVoice(context, bank, this.output, 235, 1_600);
    this.gearServo = new ServoVoice(context, bank, this.output, 155, 1_100);
  }

  updateFrame(state: AircraftAudioState): void {
    const now = this.context.currentTime;

    // --- rolling rumble -----------------------------------------------------------------------
    const voice = surfaceVoice(state.surface);
    const groundSpeed = Math.max(0, state.groundSpeedMps);
    const level = rollGain(groundSpeed, state.weightOnWheelsN, this.config.referenceWeightN);

    setTarget(this.rollLow.frequency, voice.cutoffHz, 0.1, now);
    setTarget(this.rollLevel.gain, level * voice.gain, 0.06, now);
    // Bumps arrive as fast as you drive over them. Floored at 0.5 Hz so a stationary aeroplane
    // does not have a modulator creeping toward DC.
    setTarget(this.bumpOscillator.frequency, Math.max(0.5, groundSpeed / BUMP_SPACING_M), 0.08, now);
    setTarget(this.rollAm.gain, 1 - voice.roughness * 0.5, 0.1, now);
    setTarget(this.bumpDepth.gain, voice.roughness * 0.5, 0.1, now);

    // --- the arrival ---------------------------------------------------------------------------
    if (state.onGround && !this.wasOnGround) {
      /*
       * The gear latches the sink rate at the instant of contact (see `sim/gear.ts`), which is
       * why this reads `touchdownRateMps` rather than the current vertical speed — by the time a
       * frame notices the wheels are down, the aeroplane's vertical speed is already zero and a
       * naive read produces a silent touchdown every time.
       */
      this.oneShots.play(this.bank.thump, touchdownGain(state.touchdownRateMps), 1, 0.06);
      this.oneShots.play(this.bank.chirp, touchdownChirpGain(groundSpeed) * 0.6, 1, 0.12);
    }
    this.wasOnGround = state.onGround;

    // --- flaps -----------------------------------------------------------------------------------
    const flapMoving = Math.abs(state.flapTargetDeg - state.flapAngleDeg) > 0.15;
    // Travel fraction drives the motor's pitch drift: a flap motor works harder the further out
    // the surface is, because the air load on it is rising.
    const flapTravel = clamp01(state.flapAngleDeg / 30);
    this.flapServo.updateFrame(flapMoving, flapTravel);
    if (this.flapWasMoving && !flapMoving) this.oneShots.play(this.bank.clunk, 0.35, 1, 0.05);
    this.flapWasMoving = flapMoving;

    // --- gear -------------------------------------------------------------------------------------
    // Silent unless the aeroplane actually has retractable gear; see `soundSet.ts` for why a
    // sound for a mechanism that is not fitted is worse than no sound at all.
    const gearMoving =
      this.config.retractableGear && Math.abs(state.gearTargetPosition - state.gearPosition) > 0.01;
    this.gearServo.updateFrame(gearMoving, clamp01(state.gearPosition));
    if (this.gearWasMoving && !gearMoving) {
      // Heavier and lower than a flap detent: this is a lock engaging, and it is the sound a
      // pilot listens for.
      this.oneShots.play(this.bank.clunk, 0.6, 0.72, 0.03);
    }
    this.gearWasMoving = gearMoving;
  }

  /** Fire the touchdown pair by hand, for the debug triggers. */
  debugTouchdown(sinkRateMps: number, groundSpeedMps: number): void {
    this.oneShots.play(this.bank.thump, touchdownGain(sinkRateMps), 1, 0.06);
    this.oneShots.play(this.bank.chirp, touchdownChirpGain(groundSpeedMps) * 0.6, 1, 0.12);
  }

  dispose(): void {
    this.bumpOscillator.stop();
    this.rollSource.stop();
    this.flapServo.dispose();
    this.gearServo.dispose();
    disconnectAll([
      this.rollSource,
      this.rollLow,
      this.rollAm,
      this.bumpOscillator,
      this.bumpDepth,
      this.rollLevel,
      this.output,
    ]);
  }
}

/**
 * One electric actuator: a motor whine, a gearbox hiss, and a gate.
 *
 * Built once and gated rather than created per movement, because a servo runs for a couple of
 * seconds at a time and creating an oscillator per flap selection would put an allocation on a
 * keypress path for no benefit.
 */
class ServoVoice {
  private readonly context: AudioContext;
  private readonly motor: OscillatorNode;
  private readonly noise: AudioBufferSourceNode;
  private readonly tone: BiquadFilterNode;
  private readonly level: GainNode;
  private readonly baseHz: number;

  constructor(
    context: AudioContext,
    bank: AudioBank,
    destination: AudioNode,
    baseHz: number,
    cutoffHz: number,
  ) {
    this.context = context;
    this.baseHz = baseHz;

    this.level = gainNode(context, 0);
    this.level.connect(destination);
    this.tone = lowpass(context, cutoffHz, 1.3);
    this.tone.connect(this.level);

    this.motor = context.createOscillator();
    this.motor.type = 'sawtooth';
    this.motor.frequency.value = baseHz;
    const motorLevel = gainNode(context, 0.28);
    this.motor.connect(motorLevel);
    motorLevel.connect(this.tone);
    this.motor.start(0);

    this.noise = loopingSource(context, bank.white, bank.whiteLoopEndS, 1.13, 0.9);
    const hiss = highpass(context, 900);
    const hissLevel = gainNode(context, 0.22);
    this.noise.connect(hiss);
    hiss.connect(hissLevel);
    hissLevel.connect(this.tone);
  }

  /** @param travel 0…1, how far the surface has moved — the motor pitches up as load rises. */
  updateFrame(running: boolean, travel: number): void {
    const now = this.context.currentTime;
    setTarget(this.level.gain, running ? 0.32 : 0, 0.03, now);
    setTarget(this.motor.frequency, this.baseHz * (1 + 0.22 * clamp(travel, 0, 1)), 0.15, now);
  }

  dispose(): void {
    this.motor.stop();
    this.noise.stop();
    disconnectAll([this.motor, this.noise, this.tone, this.level]);
  }
}
